import { ArrowForwardIcon } from "@chakra-ui/icons";
import {
  Heading,
  Text,
  VStack,
  Button,
  ButtonProps,
  DrawerOverlay,
} from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { UserContext } from "../../../contexts/UserContext";
import { AppDrawer } from "../../AppDrawer";
import { AppDrawerContent } from "../../modals/AppDrawerContent";
import { AddDataSourceWorkflow } from "./workflows/AddDataSourceWorkflow";

export function SelectFirstDataSource(): React.ReactElement {
  useRedirectToSignInIfUnauthenticated();

  const { finishSignUpFlow } = useContext(UserContext);
  const history = useHistory();

  return (
    <AddFirstDataSourceDrawer
      onClose={() => {
        finishSignUpFlow();
        history.replace("/");
      }}
    />
  );
}

export function AddFirstDataSourceDrawer({
  onClose,
}: {
  onClose: () => void;
}): React.ReactElement {
  const [hasStarted, setHasStarted] = useState(false);

  return (
    <AppDrawer
      isOpen={true}
      placement="right"
      onClose={onClose}
      size="sm"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <DrawerOverlay />
      <AppDrawerContent>
        {hasStarted ?
          <AddDataSourceWorkflow onComplete={onClose} enableSkipForNow />
        : <AddFirstDataSourceIntro
            onStart={() => setHasStarted(true)}
            onSkip={onClose}
          />
        }
      </AppDrawerContent>
    </AppDrawer>
  );
}

function AddFirstDataSourceIntro({
  onStart,
  onSkip,
}: {
  onStart: () => void;
  onSkip: () => void;
}): React.ReactElement {
  return (
    <VStack w="full" h="full" align="flex-start" px={6} py={8} spacing={6}>
      <VStack w="full" align="flex-start" spacing={3}>
        <Heading size="lg">Add your first data source</Heading>

        <Text>
          Connect an exchange or wallet to start syncing your transactions and
          preparing your crypto tax reports.
        </Text>

        <Text>
          We’ll guide you through the setup step by step. You can also skip this
          for now and start adding data sources later from your account.
        </Text>
      </VStack>

      <VStack w="full" align="stretch" spacing={6}>
        <Button colorScheme="primary" onClick={onStart}>
          Add data source
        </Button>

        <SkipForNowButton onClick={onSkip} />
      </VStack>
    </VStack>
  );
}

export function SkipForNowButton(props: ButtonProps) {
  const history = useHistory();
  const { finishSignUpFlow } = useContext(UserContext);

  return (
    <Button
      variant="ghost"
      colorScheme="gray"
      size="sm"
      rightIcon={<ArrowForwardIcon />}
      onClick={() => {
        finishSignUpFlow();
        history.replace("/");
      }}
      {...props}
    >
      Skip for now
    </Button>
  );
}

/**
 * redirect back to the sign-in page if the user is here without an access token,
 * as it means they haven't registered yet
 */
function useRedirectToSignInIfUnauthenticated() {
  const { userDetails } = useContext(UserContext);
  const history = useHistory();

  useEffect(() => {
    if (!userDetails?.accessToken) {
      history.replace("/sign-in");
    }
  }, [history, userDetails?.accessToken]);
}
